import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { BrandLogo, PillArrow } from './Logo.jsx';
import { Sidebar } from './Sidebar.jsx';
import { StoryView } from './StoryView.jsx';
import { Home } from './Home.jsx';
import { Search } from './Search.jsx';
import { createBridge } from './bridge.js';
import { readUrl, writeUrl } from './url.js';
import { buildTree, ancestorsOf } from './tree.js';
import { createPluginHost } from './plugins.js';
import { Segments } from './glide.jsx';

const bridge = createBridge();
// For debugging and for tools that drive the shell (parity checks, tests).
window.__EDL_DOCS_BRIDGE__ = bridge;

function reducer(state, action) {
  switch (action.type) {
    case 'index':
      return { ...state, index: action.index };
    case 'select':
      return { ...state, id: action.id, viewMode: action.viewMode, story: action.keepStory ? state.story : null, status: action.id ? 'loading' : 'idle', error: null };
    case 'globals':
      return { ...state, globals: { ...state.globals, ...action.globals } };
    case 'storyPrepared':
      if (action.msg.id !== state.id) return state;
      return { ...state, story: { argTypes: action.msg.argTypes, args: action.msg.args, initialArgs: action.msg.initialArgs, parameters: action.msg.parameters, name: action.msg.name, title: action.msg.title, server: action.msg.server } };
    case 'args':
      if (!state.story) return state;
      return { ...state, story: { ...state.story, args: { ...state.story.args, ...action.args } } };
    case 'resetArgs':
      if (!state.story) return state;
      return { ...state, story: { ...state.story, args: { ...state.story.initialArgs } } };
    case 'rendered':
      if (action.msg.id !== state.id) return state;
      return { ...state, status: 'rendered', error: null, story: state.story && action.msg.server ? { ...state.story, server: true } : state.story };
    case 'error':
      if (action.msg.id !== state.id) return state;
      return { ...state, status: 'error', error: action.msg };
    case 'tick':
      return { ...state, tick: state.tick + 1 };
    default:
      return state;
  }
}

export function App({ boot }) {
  const { config } = boot;
  const initialUrl = useMemo(readUrl, []);
  const defaultGlobals = Object.fromEntries(Object.entries(config.globals ?? {}).map(([k, v]) => [k, v.default]));
  const [state, dispatch] = useReducer(reducer, {
    index: boot.index,
    id: initialUrl.id ?? config.defaultStory ?? null,
    viewMode: initialUrl.viewMode ?? null,
    globals: { ...defaultGlobals, ...readStoredGlobals(), ...initialUrl.globals },
    story: null,
    status: initialUrl.id ? 'loading' : 'idle',
    error: null,
    tick: 0,
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const urlArgs = useRef(initialUrl.args);
  const stateRef = useRef(state);
  stateRef.current = state;

  const entry = state.id ? state.index.entries[state.id] : null;
  const tree = useMemo(() => buildTree(state.index), [state.index]);

  const navigate = useCallback((target, { replace = false } = {}) => {
    const id = typeof target === 'string' ? target : target?.id ?? null;
    const viewMode = id ? (id.endsWith('--docs') ? 'docs' : 'story') : null;
    dispatch({ type: 'select', id, viewMode });
    writeUrl({ id, viewMode, globals: nonDefault(stateRef.current.globals, defaultGlobals) }, { replace });
    setNavOpen(false);
  }, []);

  const pluginsRef = useRef(null);
  if (!pluginsRef.current) {
    pluginsRef.current = createPluginHost({
      getState: () => ({ ...stateRef.current, entry: stateRef.current.id ? stateRef.current.index.entries[stateRef.current.id] : null, config }),
      navigate,
      refresh: () => dispatch({ type: 'tick' }),
    });
  }
  const plugins = pluginsRef.current;

  useEffect(() => {
    plugins.load(boot.plugins ?? []);
  }, []);

  // Preview → shell
  useEffect(
    () =>
      bridge.onMessage((msg) => {
        if (msg.type === 'ready') sendSelect();
        else if (msg.type === 'storyPrepared') dispatch({ type: 'storyPrepared', msg });
        else if (msg.type === 'rendered') dispatch({ type: 'rendered', msg });
        else if (msg.type === 'error') dispatch({ type: 'error', msg });
        else if (msg.type === 'navigate') navigate(msg.id);
      }),
    [],
  );

  // Shell → preview: the selection, with any args changed in the panel so a
  // preview reload lands where the user was.
  function sendSelect() {
    const s = stateRef.current;
    if (!s.id) return;
    const changedArgs = s.story ? diffArgs(s.story.args, s.story.initialArgs) : null;
    const args = { ...(urlArgs.current ?? {}), ...(changedArgs ?? {}) };
    if (!bridge.isReady()) return;
    bridge.send({ type: 'select', id: s.id, viewMode: s.viewMode, globals: s.globals, index: s.index, args: Object.keys(args).length ? args : undefined });
    urlArgs.current = null;
  }

  useEffect(() => {
    if (!state.id) return;
    sendSelect();
    plugins.emit('select', { id: state.id, entry });
  }, [state.id]);

  useEffect(() => {
    bridge.send({ type: 'setGlobals', globals: state.globals });
    document.documentElement.dataset.previewTheme = state.globals.theme ?? '';
    try {
      localStorage.setItem('edl-docs:globals', JSON.stringify(state.globals));
    } catch {}
  }, [JSON.stringify(state.globals)]);

  // Live index (dev only): story files added, removed or renamed.
  useEffect(() => {
    if (config.mode !== 'dev' || typeof EventSource === 'undefined') return undefined;
    const es = new EventSource('/__edl/events');
    es.addEventListener('index', (e) => {
      const index = JSON.parse(e.data);
      dispatch({ type: 'index', index });
      bridge.send({ type: 'index', index });
    });
    return () => es.close();
  }, []);

  // Back / forward
  useEffect(() => {
    const onPop = () => {
      const u = readUrl();
      dispatch({ type: 'select', id: u.id, viewMode: u.viewMode });
      if (Object.keys(u.globals).length) dispatch({ type: 'globals', globals: u.globals });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // ⌘K / "/" opens search
  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName ?? '') || document.activeElement?.isContentEditable;
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.title = entry ? `${entry.title.split('/').at(-1)}${entry.type === 'story' ? ` · ${entry.name}` : ''} · ${config.title}` : `${config.title} · ${config.brand?.name ? `${config.brand.name} docs` : 'edl docs'}`;
  }, [entry?.id]);

  const setGlobals = (globals) => {
    dispatch({ type: 'globals', globals });
    writeUrl({ id: state.id, viewMode: state.viewMode, globals: nonDefault({ ...state.globals, ...globals }, defaultGlobals) }, { replace: true });
  };

  const updateArgs = (args) => {
    dispatch({ type: 'args', args });
    bridge.send({ type: 'updateArgs', id: state.id, args });
  };
  const resetArgs = () => {
    dispatch({ type: 'resetArgs' });
    bridge.send({ type: 'resetArgs', id: state.id });
  };

  const openAncestors = ancestorsOf(entry);

  return (
    <div className={`shell${navOpen ? ' is-nav-open' : ''}`}>
      <header className="shell-top">
        <div className="shell-top__brand">
          <a
            className="shell-top__logo"
            href="./"
            onClick={(e) => {
              e.preventDefault();
              navigate(null);
            }}
            aria-label={`${config.title} home`}
          >
            <BrandLogo brand={config.brand} height={config.brand?.logo ? 20 : 30} />
          </a>
          {/* company | client: an agency's docs name the client beside the
              agency's logo; a direct client's title is their own name, which
              the logo already says. */}
          {sameName(config.title, config.brand?.name) ? null : (
            <>
              <span className="shell-top__divider" aria-hidden="true" />
              <span className="shell-top__project">{config.title}</span>
            </>
          )}
        </div>

        <button type="button" className="shell-search-trigger edl-underline" onClick={() => setSearchOpen(true)} aria-label="Search components">
          <span>Search components</span>
          <svg className="shell-search-trigger__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-4-4" />
          </svg>
          <kbd>⌘K</kbd>
        </button>

        <div className="shell-top__actions">
          {Object.entries(config.globals ?? {}).map(([key, g]) => (
            <Segments key={key} className="shell-segmented" label={g.label} value={state.globals[key]} options={g.options} onChange={(v) => setGlobals({ [key]: v })} />
          ))}
          {(config.links ?? []).map((l) => (
            <a key={l.href} className="edl-pill edl-pill--small edl-pill--ghost shell-top__link" href={l.href} target="_blank" rel="noreferrer">
              {l.label}
              <span className="edl-pill__disc">
                <PillArrow />
              </span>
            </a>
          ))}
          <button type="button" className="edl-pill edl-pill--small edl-pill--plain shell-menu-button" onClick={() => setNavOpen((v) => !v)} aria-expanded={navOpen}>
            {navOpen ? 'Close' : 'Menu'}
          </button>
        </div>
      </header>

      <div className="shell-body">
        <Sidebar tree={tree} activeId={state.id} openKeys={openAncestors} onSelect={navigate} badgesFor={plugins.badgesFor} index={state.index} />
        <main className="shell-main" id="main">
          <StoryView
            bridge={bridge}
            entry={entry}
            mode={!state.id ? 'home' : !entry ? 'missing' : entry.type === 'docs' ? 'docs' : 'story'}
            state={state}
            config={config}
            onArgs={updateArgs}
            onResetArgs={resetArgs}
            onNavigate={navigate}
            plugins={plugins}
          >
            {!state.id && <Home config={config} index={state.index} tree={tree} onNavigate={navigate} onSearch={() => setSearchOpen(true)} />}
            {state.id && !entry && <NotFound id={state.id} onHome={() => navigate(null)} />}
          </StoryView>
        </main>
      </div>

      {searchOpen && <Search index={state.index} onClose={() => setSearchOpen(false)} onSelect={(id) => { setSearchOpen(false); navigate(id); }} />}
    </div>
  );
}

function NotFound({ id, onHome }) {
  return (
    <div className="shell-empty">
      <p className="edl-label shell-empty__label">Not found</p>
      <h1 className="shell-empty__title">Nothing lives at “{id}” any more.</h1>
      <p className="shell-empty__text">The story may have been renamed or removed.</p>
      <button type="button" className="edl-pill" onClick={onHome}>
        Back to the overview
        <span className="edl-pill__disc">
          <PillArrow />
        </span>
      </button>
    </div>
  );
}

function diffArgs(args = {}, initial = {}) {
  const out = {};
  for (const [k, v] of Object.entries(args)) {
    if (v && typeof v === 'object' && v.__edlUnserialisable) continue;
    if (JSON.stringify(v) !== JSON.stringify(initial[k])) out[k] = v;
  }
  return Object.keys(out).length ? out : null;
}

function nonDefault(globals, defaults) {
  return Object.fromEntries(Object.entries(globals).filter(([k, v]) => defaults[k] !== v));
}

function readStoredGlobals() {
  try {
    return JSON.parse(localStorage.getItem('edl-docs:globals') ?? '{}') ?? {};
  } catch {
    return {};
  }
}

// "Accutics" and " accutics " are the same company.
function sameName(a, b) {
  return Boolean(a && b) && a.trim().toLowerCase() === b.trim().toLowerCase();
}
